<template>
<div class="mi-root" :style="themePageStyle">

  <!-- Topbar -->
  <nav class="mi-topbar"><div class="container d-flex align-items-center justify-content-between py-2">
    <div class="d-flex align-items-center" style="gap:12px;">
      <back-button class="me-2" />
      <a href="javascript:void(0)" @click="$router.push('/')"><img src="/assets/images/flockr_logo.png" alt="Flockr" style="height:34px;" /></a>
      <span class="fw-semibold" style="color:var(--fm-h,#F5F0E8);font-size:15px;">Market Intelligence</span>
    </div>
    <div class="d-flex gap-2">
      <button class="btn btn-sm btn-outline-warning d-none d-md-inline-block" @click="$router.push('/dashboard')"><i class="mdi mdi-view-dashboard me-1"></i>Dashboard</button>
      <button class="btn btn-sm btn-warning" @click="$router.push('/marketplace')"><i class="mdi mdi-storefront-outline me-1"></i>Marketplace</button>
    </div>
  </div></nav>

  <div class="container mi-container">
    <!-- Hero -->
    <div class="text-center mb-4">
      <h4 class="fw-bold" style="color:var(--fm-h,#F5F0E8);">📈 Livestock Market Intelligence</h4>
      <p style="color:var(--fm-s,#6B6356);font-size:13px;max-width:640px;margin:0 auto;">
        Insights from verified extension officers, veterinarians and market analysts across Africa.
        The auto-generated price grid is being rebuilt with fresh data — until then, rely on the insights below and the market reports.
      </p>
    </div>

    <!-- Dev-Requests #3 — Expert Insights feed (hides the auto-generated price grid
         until enough transactions are on the platform to produce reliable averages). -->
    <div class="mb-4">
      <div class="d-flex justify-content-between align-items-center mb-3">
        <h5 style="color:var(--fm-h,#F5F0E8);margin:0;">
          <i class="mdi mdi-account-star me-2" style="color:#BFA164;"></i>Expert Insights
        </h5>
        <span style="color:var(--fm-s,#6B6356);font-size:11px;">
          {{ insights.length }} insight{{ insights.length===1?'':'s' }}
        </span>
      </div>
      <div v-if="loadingInsights" class="text-center py-4"><div class="spinner-border spinner-border-sm text-warning"></div></div>
      <div v-else-if="!insights.length" class="text-center py-4" style="color:var(--fm-s,#6B6356);font-size:13px;">
        <i class="mdi mdi-account-star-outline" style="font-size:32px;opacity:.5;"></i>
        <div class="mt-2">No expert insights published yet. Check back soon.</div>
      </div>
      <div v-else class="row g-3">
        <div class="col-md-6 col-lg-4" v-for="ins in insights" :key="ins.id">
          <div class="mi-insight">
            <div class="mi-insight-header">
              <span v-if="ins.is_pinned" class="mi-pinned"><i class="mdi mdi-pin"></i> Pinned</span>
              <span v-if="ins.species_name" class="mi-tag">{{ ins.species_name }}</span>
              <span v-if="ins.county_name" class="mi-tag">{{ ins.county_name }}</span>
            </div>
            <div class="mi-insight-title">{{ ins.title }}</div>
            <div class="mi-insight-body">{{ ins.body }}</div>
            <div class="mi-insight-footer">
              <div>
                <div class="mi-author">
                  <i class="mdi mdi-account-check" style="color:#BFA164;"></i>
                  {{ ins.display_author || 'Flockr Team' }}
                </div>
                <div v-if="ins.author_role" class="mi-author-role">{{ ins.author_role }}</div>
              </div>
              <div class="mi-updated"><i class="mdi mdi-clock-outline me-1"></i>Updated {{ fmtDate(ins.updated_at) }}</div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Weekly Market Days — gate figures from physical livestock markets (Rumuruti etc.) -->
    <div class="mb-4 mt-5">
      <div class="d-flex justify-content-between align-items-center mb-3">
        <h5 style="color:var(--fm-h,#F5F0E8);margin:0;">
          <i class="mdi mdi-cow me-2" style="color:#BFA164;"></i>Weekly Market Days
        </h5>
        <span style="color:var(--fm-s,#6B6356);font-size:11px;">{{ marketDays.length }} report{{ marketDays.length===1?'':'s' }}</span>
      </div>
      <div v-if="loadingMarketDays" class="text-center py-4"><div class="spinner-border spinner-border-sm text-warning"></div></div>
      <div v-else-if="!marketDays.length" class="text-center py-4" style="color:var(--fm-s,#6B6356);font-size:13px;">
        No market day reports published yet.
      </div>
      <div v-else class="row g-3">
        <div class="col-12" :class="mdExpandedId===d.id ? '' : 'col-md-6 col-lg-4'" v-for="d in marketDays" :key="d.id">
          <div class="mi-md-card" :class="{active: mdExpandedId===d.id}" @click="toggleMarketDay(d)">
            <div class="d-flex justify-content-between align-items-start">
              <div>
                <div class="mi-spec">{{ d.market_name }} Market</div>
                <div class="mi-county"><i class="mdi mdi-map-marker me-1"></i>{{ d.county_name || '—' }} · {{ fmtMdDate(d.report_date) }}</div>
              </div>
              <i class="mdi" :class="mdExpandedId===d.id ? 'mdi-chevron-up' : 'mdi-chevron-down'" style="color:#BFA164;font-size:20px;"></i>
            </div>
            <div class="d-flex gap-2 flex-wrap mt-2">
              <span v-for="t in d.totals" :key="t.species" class="mi-md-chip">
                {{ t.species }} <b>{{ fNum(t.total) }}</b>
              </span>
            </div>

            <!-- Expanded detail -->
            <div v-if="mdExpandedId===d.id" @click.stop style="cursor:default;">
              <div v-if="loadingMdDetail" class="text-center py-4"><div class="spinner-border spinner-border-sm text-warning"></div></div>
              <template v-else-if="mdDetail">
                <!-- Totals -->
                <div class="mi-md-sec"><i class="mdi mdi-sigma me-1"></i>Animals presented</div>
                <div class="table-responsive">
                  <table class="mi-md-table">
                    <thead><tr><th>Species</th><th>Slaughter</th><th>Breeding</th><th>Total</th></tr></thead>
                    <tbody>
                      <tr v-for="t in mdDetail.totals" :key="'t'+t.id">
                        <td style="font-weight:600;">{{ t.species }}</td>
                        <td>{{ fNum(t.slaughter) }}</td>
                        <td>{{ fNum(t.breeding) }}</td>
                        <td style="color:#BFA164;font-weight:700;">{{ fNum(t.total) }}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <!-- Prices -->
                <div class="mi-md-sec"><i class="mdi mdi-tag-multiple me-1"></i>Trending market prices</div>
                <div class="table-responsive">
                  <table class="mi-md-table">
                    <thead><tr><th>Species</th><th>Sex</th><th>Age</th><th>Price</th></tr></thead>
                    <tbody>
                      <tr v-for="p in mdDetail.prices" :key="'p'+p.id">
                        <td style="font-weight:600;">{{ p.species }}</td>
                        <td style="text-transform:capitalize;">{{ p.sex }}</td>
                        <td style="text-transform:capitalize;">{{ p.age_group }}</td>
                        <td style="color:#BFA164;font-weight:700;">KES {{ fNum(p.price) }}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <!-- Movements -->
                <div class="row g-3">
                  <div class="col-lg-6" v-for="scope in ['outside_county','within_county']" :key="scope" v-show="mdMovements(scope).length">
                    <div class="mi-md-sec"><i class="mdi mdi-truck-fast-outline me-1"></i>{{ scope==='outside_county' ? 'Moved outside the county' : 'Within the county' }}</div>
                    <div class="table-responsive">
                      <table class="mi-md-table">
                        <thead><tr><th>Destination</th><th>Species</th><th>Slaughter</th><th>Breeding</th></tr></thead>
                        <tbody>
                          <tr v-for="m in mdMovements(scope)" :key="'m'+m.id">
                            <td>{{ m.destination }}</td>
                            <td>{{ m.species }}</td>
                            <td>{{ Number(m.slaughter) ? fNum(m.slaughter) : '—' }}</td>
                            <td>{{ Number(m.breeding) ? fNum(m.breeding) : '—' }}</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>

                <div v-if="mdDetail.notes" style="color:var(--fm-s,#6B6356);font-size:11px;margin-top:10px;">
                  <i class="mdi mdi-information-outline me-1"></i>{{ mdDetail.notes }}
                </div>
              </template>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Historical movement — yearly county×month matrix from Rumuruti (2024–2025 backfill) -->
    <div v-if="histYears.length" class="mb-4">
      <div class="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
        <h5 style="color:var(--fm-h,#F5F0E8);margin:0;">
          <i class="mdi mdi-history me-2" style="color:#BFA164;"></i>Historical Movement — Rumuruti Market
        </h5>
        <div class="d-flex gap-2">
          <button v-for="y in histYears" :key="y.year" class="mi-hist-tab" :class="{active: histYear===y.year}" @click="histYear=y.year">{{ y.year }}</button>
        </div>
      </div>
      <div class="mi-md-card active" style="cursor:default;">
        <div class="d-flex gap-2 flex-wrap">
          <span v-for="(v,sp) in (histActive.summary||{})" :key="sp" class="mi-md-chip">{{ sp }} <b>{{ fNum(v) }}</b></span>
        </div>
        <div class="mi-md-sec"><i class="mdi mdi-truck-fast-outline me-1"></i>Animals moved per destination county · {{ histYear }}</div>
        <div class="table-responsive">
          <table class="mi-md-table" style="min-width:940px;">
            <thead>
              <tr><th>County</th><th>Species</th><th v-for="m in histMonths" :key="m" style="text-align:right;">{{ m }}</th><th style="text-align:right;">Total</th></tr>
            </thead>
            <tbody>
              <tr v-for="(r,idx) in (histActive.rows||[])" :key="idx">
                <td>{{ r.county }}</td>
                <td>{{ r.species }}</td>
                <td v-for="i in 12" :key="i" style="text-align:right;">{{ r['m'+i]===null ? '·' : (Number(r['m'+i]) ? fNum(r['m'+i]) : '—') }}</td>
                <td style="text-align:right;color:#BFA164;font-weight:600;">{{ fNum(r.total) }}</td>
              </tr>
            </tbody>
          </table>
        </div>
        <div style="color:var(--fm-s,#6B6356);font-size:11px;margin-top:10px;">
          <i class="mdi mdi-information-outline me-1"></i>County veterinary movement figures, Jan 2024 – Nov 2025. "·" = month not covered by the source records.
        </div>
      </div>
    </div>

    <!-- Auto-generated price grid — hidden while data is thin. Set showPrices=true
         (localStorage.flockr_show_price_grid=1) if you want to review it as admin. -->
    <div v-if="showPriceGrid" class="mi-archived-banner mb-3">
      <i class="mdi mdi-archive-outline me-1"></i>
      <b>Price grid is being rebuilt.</b> The sample size is too small to show reliable averages yet — treat the numbers below as directional only.
    </div>

    <!-- Filters -->
    <div v-if="showPriceGrid" class="mi-filters mb-4">
      <div class="row g-2">
        <div class="col-md-4">
          <label class="mi-lbl">Species</label>
          <select v-model="filter.speciesId" class="form-select mi-input" @change="loadPrices">
            <option value="">All species</option>
            <option v-for="s in species" :key="s.id" :value="s.id">{{ s.name }}</option>
          </select>
        </div>
        <div class="col-md-4">
          <label class="mi-lbl">County</label>
          <select v-model="filter.countyId" class="form-select mi-input" @change="loadPrices">
            <option value="">All counties</option>
            <option v-for="c in counties" :key="c.id" :value="c.id">{{ c.name }}</option>
          </select>
        </div>
        <div class="col-md-4">
          <label class="mi-lbl">Period</label>
          <select v-model="filter.periodType" class="form-select mi-input" @change="loadPrices">
            <option value="">Any</option>
            <option value="weekly">Weekly</option>
            <option value="monthly">Monthly</option>
            <option value="daily">Daily</option>
          </select>
        </div>
      </div>
    </div>

    <!-- Prices grid — gated behind showPriceGrid until fresh data is ready. -->
    <div v-if="showPriceGrid && loadingPrices" class="text-center py-5"><div class="spinner-border text-warning"></div></div>
    <div v-else-if="showPriceGrid && !prices.length" class="text-center py-5">
      <i class="mdi mdi-chart-line-variant" style="font-size:64px;color:var(--fm-s,#3A3530);"></i>
      <h5 class="mt-3" style="color:var(--fm-h,#F5F0E8);">No price data yet</h5>
      <p style="color:var(--fm-s,#6B6356);">Check back soon — admin publishes prices weekly.</p>
    </div>
    <div v-else-if="showPriceGrid" class="row g-3">
      <div class="col-md-6 col-lg-4" v-for="p in prices" :key="p.id">
        <div class="mi-card" :class="{active: trendKey === p.species_id+'-'+p.county_id+'-'+(p.breed_id||0)}" @click="loadTrend(p)">
          <div class="d-flex justify-content-between align-items-start mb-2">
            <div>
              <div class="mi-spec">{{ p.species_name }}<span v-if="p.breed_name"> · {{ p.breed_name }}</span></div>
              <div class="mi-county"><i class="mdi mdi-map-marker me-1"></i>{{ p.county_name }}</div>
            </div>
            <span class="mi-period">{{ p.period_type }}</span>
          </div>
          <div class="mi-avg">KES {{ fNum(p.avg_price) }}<span class="mi-unit">/{{ p.price_unit === 'per_kg' ? 'kg' : 'animal' }}</span></div>
          <div class="mi-range" v-if="p.min_price || p.max_price">
            <span><i class="mdi mdi-arrow-down me-1" style="color:#FF6B6B;"></i>KES {{ fNum(p.min_price) }}</span>
            <span><i class="mdi mdi-arrow-up me-1" style="color:#50C878;"></i>KES {{ fNum(p.max_price) }}</span>
          </div>
          <div class="mi-meta">
            <span><i class="mdi mdi-calendar me-1"></i>{{ p.record_date }}</span>
            <span v-if="p.sample_size > 0">· {{ p.sample_size }} txns</span>
            <span v-if="p.total_volume" >· {{ p.total_volume }} animals</span>
          </div>
          <div class="mi-source">{{ humanizeSource(p.source) }}</div>
        </div>
      </div>
    </div>

    <!-- Trend chart (lazy-loaded after click) -->
    <div v-if="showPriceGrid && trendKey" class="mi-trend-card mt-4">
      <div class="d-flex justify-content-between align-items-center mb-2">
        <h6 style="color:var(--fm-h,#F5F0E8);margin:0;">
          <i class="mdi mdi-chart-line me-1" style="color:#BFA164;"></i>
          {{ trendLabel }}
        </h6>
        <button class="btn btn-sm btn-link" style="color:var(--fm-s,#6B6356);text-decoration:none;font-size:11px;" @click="trendKey=''; trendSeries=[]">
          <i class="mdi mdi-close"></i> Close
        </button>
      </div>
      <div v-if="loadingTrend" class="text-center py-4"><div class="spinner-border spinner-border-sm text-warning"></div></div>
      <div v-else-if="!trendSeries.length" class="text-center py-3" style="color:var(--fm-s,#6B6356);font-size:12px;">No historical data for this combination yet.</div>
      <div v-else>
        <!-- Inline SVG sparkline + axis labels -->
        <svg :viewBox="'0 0 '+chartW+' '+chartH" :width="'100%'" :height="chartH" preserveAspectRatio="none" class="mi-chart">
          <polyline :points="chartLine" fill="none" stroke="#BFA164" stroke-width="2.5" stroke-linejoin="round" />
          <polyline :points="chartArea" fill="rgba(191,161,100,.12)" stroke="none" />
          <circle v-for="(p,i) in chartPoints" :key="i" :cx="p.x" :cy="p.y" r="3" fill="#BFA164" />
        </svg>
        <div class="d-flex justify-content-between" style="font-size:10px;color:var(--fm-s,#6B6356);margin-top:6px;">
          <span>{{ trendSeries[0].record_date }}</span>
          <span>{{ trendSeries[trendSeries.length-1].record_date }}</span>
        </div>
        <div class="d-flex justify-content-between" style="font-size:11px;color:var(--fm-m,#A09888);margin-top:4px;">
          <span>Min: <strong style="color:#FF6B6B;">KES {{ fNum(trendStats.min) }}</strong></span>
          <span>Avg: <strong style="color:#BFA164;">KES {{ fNum(trendStats.avg) }}</strong></span>
          <span>Max: <strong style="color:#50C878;">KES {{ fNum(trendStats.max) }}</strong></span>
          <span>Change: <strong :style="{color: trendStats.changePct >= 0 ? '#50C878' : '#FF6B6B'}">{{ trendStats.changePct >= 0 ? '+' : '' }}{{ trendStats.changePct.toFixed(1) }}%</strong></span>
        </div>
      </div>
    </div>

    <!-- Reports -->
    <div class="mt-5">
      <div class="d-flex justify-content-between align-items-center mb-3">
        <h5 style="color:var(--fm-h,#F5F0E8);margin:0;"><i class="mdi mdi-file-chart me-2" style="color:#BFA164;"></i>Market Reports</h5>
        <span style="color:var(--fm-s,#6B6356);font-size:11px;">{{ reports.length }} report{{ reports.length===1?'':'s' }}</span>
      </div>
      <div v-if="loadingReports" class="text-center py-4"><div class="spinner-border spinner-border-sm text-warning"></div></div>
      <div v-else-if="!reports.length" class="text-center py-4" style="color:var(--fm-s,#6B6356);font-size:13px;">
        No reports published yet.
      </div>
      <div v-else class="row g-3">
        <div class="col-md-6 col-lg-4" v-for="r in reports" :key="r.id">
          <div class="mi-report" @click="openReport(r)">
            <div class="mi-report-cover" :style="r.cover_image_url ? ('background-image:url('+imgUrl(r.cover_image_url)+')') : ''">
              <i v-if="!r.cover_image_url" class="mdi mdi-file-chart"></i>
              <span class="mi-report-type">{{ r.report_type }}</span>
            </div>
            <div class="mi-report-body">
              <div class="mi-report-title">{{ r.title }}</div>
              <div class="mi-report-meta">
                <span><i class="mdi mdi-calendar me-1"></i>{{ r.report_date }}</span>
                <span v-if="r.county_name">· {{ r.county_name }}</span>
                <span v-if="r.species_name">· {{ r.species_name }}</span>
              </div>
              <div class="mi-report-excerpt">{{ r.excerpt }}…</div>
              <div class="mi-report-stats">
                <span><i class="mdi mdi-eye-outline me-1"></i>{{ r.view_count }}</span>
                <span v-if="r.download_count"><i class="mdi mdi-download me-1"></i>{{ r.download_count }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

  </div>
</div>
</template>

<style>
:root{--fm-h:#F5F0E8;--fm-s:#6B6356;--fm-m:#A09888;--fm-bg1:#141414;--fm-bg2:#0F0F0F;--fm-bg3:#1E1E1E;--fm-br:#1E1E1E}
html[data-theme="light"]{--fm-h:#1A1A1A;--fm-s:#777;--fm-m:#555;--fm-bg1:#FFFFFF;--fm-bg2:#F7F5F2;--fm-bg3:#F0EBE4;--fm-br:#E8E2DA}
html[data-theme="light"] body{background:#F7F5F2!important;color:#1A1A1A!important}
html[data-theme="light"] .mi-root{background:#F7F5F2!important;color:#1A1A1A!important}
html[data-theme="light"] .mi-topbar{background:rgba(255,255,255,.95)!important;border-bottom-color:#E8E2DA!important}
html[data-theme="light"] .mi-card,
html[data-theme="light"] .mi-trend-card,
html[data-theme="light"] .mi-report{background:#FFFFFF!important;border-color:#E8E2DA!important}
html[data-theme="light"] .mi-input{background:#FFFFFF!important;border-color:#E0D8D0!important;color:#1A1A1A!important}
</style>

<style scoped>
.mi-root{background:var(--fm-bg2,#0A0A0A);min-height:100vh;color:var(--fm-h,#F5F0E8)}
.mi-topbar{position:sticky;top:0;z-index:100;padding:12px 0;background:var(--fm-bg1,#141414);backdrop-filter:blur(10px);border-bottom:1px solid var(--fm-br,#1E1E1E)}
.mi-container{padding-top:80px;padding-bottom:60px;max-width:1100px}
.mi-filters{background:var(--fm-bg1,#141414);border:1px solid var(--fm-br,#1E1E1E);border-radius:10px;padding:14px 16px}
.mi-lbl{color:var(--fm-s,#6B6356);font-size:10px;text-transform:uppercase;letter-spacing:.5px;font-weight:600;display:block;margin-bottom:4px}
.mi-input{background:var(--fm-bg2,#0F0F0F)!important;border:1px solid var(--fm-br,#1E1E1E);color:var(--fm-h,#F5F0E8)!important;font-size:13px}
.mi-input:focus{border-color:#BFA164;box-shadow:0 0 0 2px rgba(191,161,100,.15)}

.mi-card{background:var(--fm-bg1,#141414);border:1px solid var(--fm-br,#1E1E1E);border-radius:12px;padding:16px;cursor:pointer;transition:all .2s;height:100%}
.mi-card:hover{border-color:#BFA164;transform:translateY(-2px);box-shadow:0 8px 24px rgba(0,0,0,.2)}
.mi-card.active{border-color:#BFA164;background:rgba(191,161,100,.05)}
.mi-spec{color:var(--fm-h,#F5F0E8);font-weight:700;font-size:14px}
.mi-county{color:var(--fm-m,#A09888);font-size:11px}
.mi-period{font-size:10px;padding:2px 8px;border-radius:6px;background:rgba(191,161,100,.1);color:#BFA164;text-transform:capitalize}
.mi-avg{color:#BFA164;font-weight:700;font-size:24px;margin:6px 0 2px}
.mi-unit{color:var(--fm-s,#6B6356);font-size:11px;font-weight:400;margin-left:4px}
.mi-range{display:flex;gap:14px;font-size:11px;color:var(--fm-m,#A09888);margin-bottom:8px}
.mi-meta{font-size:10px;color:var(--fm-s,#6B6356);display:flex;gap:6px;flex-wrap:wrap}
.mi-source{font-size:9px;color:var(--fm-s,#6B6356);text-transform:uppercase;letter-spacing:.5px;margin-top:6px;padding-top:6px;border-top:1px dashed var(--fm-br,#1E1E1E)}

.mi-md-card{background:var(--fm-bg1,#141414);border:1px solid var(--fm-br,#1E1E1E);border-radius:12px;padding:16px;cursor:pointer;transition:all .2s;height:100%}
.mi-md-card:hover{border-color:#BFA164;transform:translateY(-2px)}
.mi-md-card.active{border-color:#BFA164;background:rgba(191,161,100,.04);transform:none}
.mi-md-chip{background:rgba(191,161,100,.12);color:var(--fm-h,#F5F0E8);font-size:11px;padding:3px 10px;border-radius:14px}
.mi-md-chip b{color:#BFA164}
.mi-md-sec{color:#BFA164;font-size:11px;font-weight:700;text-transform:uppercase;letter-spacing:.6px;margin:16px 0 6px}
.mi-md-table{width:100%;font-size:12px;color:var(--fm-m,#A09888);border-collapse:collapse}
.mi-md-table th{color:var(--fm-s,#6B6356);font-size:10px;text-transform:uppercase;letter-spacing:.5px;font-weight:600;text-align:left;padding:6px 10px;border-bottom:1px solid var(--fm-br,#1E1E1E)}
.mi-md-table td{padding:6px 10px;border-bottom:1px dashed var(--fm-br,#1E1E1E)}
.mi-md-table tr:last-child td{border-bottom:none}
.mi-hist-tab{background:transparent;border:1px solid var(--fm-br,#1E1E1E);color:var(--fm-m,#A09888);font-size:12px;font-weight:600;padding:4px 16px;border-radius:16px;cursor:pointer;transition:all .2s}
.mi-hist-tab:hover{border-color:#BFA164;color:#BFA164}
.mi-hist-tab.active{background:rgba(191,161,100,.12);border-color:#BFA164;color:#BFA164}
html[data-theme="light"] .mi-md-card{background:#FFFFFF!important;border-color:#E8E2DA!important}

.mi-trend-card{background:var(--fm-bg1,#141414);border:1px solid var(--fm-br,#1E1E1E);border-radius:12px;padding:18px}
.mi-chart{width:100%;display:block}

.mi-insight{background:var(--fm-bg1,#141414);border:1px solid var(--fm-br,#1E1E1E);border-radius:12px;padding:16px;height:100%;display:flex;flex-direction:column;gap:8px;transition:all .2s}
.mi-insight:hover{border-color:#BFA164;transform:translateY(-1px)}
.mi-insight-header{display:flex;gap:6px;flex-wrap:wrap;align-items:center}
.mi-pinned{background:rgba(255,193,7,.15);color:#FFC107;font-size:9px;padding:2px 6px;border-radius:6px;font-weight:600;text-transform:uppercase;letter-spacing:.4px}
.mi-tag{background:rgba(191,161,100,.14);color:#BFA164;font-size:9px;padding:2px 6px;border-radius:6px;font-weight:600}
.mi-insight-title{color:var(--fm-h,#F5F0E8);font-weight:700;font-size:15px;line-height:1.4}
.mi-insight-body{color:var(--fm-m,#A09888);font-size:12px;line-height:1.6;flex:1;white-space:pre-line}
.mi-insight-footer{display:flex;justify-content:space-between;align-items:flex-end;font-size:11px;padding-top:8px;border-top:1px dashed var(--fm-br,#1E1E1E)}
.mi-author{color:var(--fm-h,#F5F0E8);font-weight:600;font-size:11px}
.mi-author-role{color:var(--fm-s,#6B6356);font-size:10px}
.mi-updated{color:var(--fm-s,#6B6356);font-size:10px}
.mi-archived-banner{background:rgba(191,161,100,.08);border:1px dashed #BFA164;border-radius:8px;padding:10px 14px;color:var(--fm-h,#F5F0E8);font-size:12px}
html[data-theme="light"] .mi-insight{background:#FFFFFF!important;border-color:#E8E2DA!important}

.mi-report{background:var(--fm-bg1,#141414);border:1px solid var(--fm-br,#1E1E1E);border-radius:12px;overflow:hidden;cursor:pointer;transition:all .2s;height:100%;display:flex;flex-direction:column}
.mi-report:hover{border-color:#BFA164;transform:translateY(-2px)}
.mi-report-cover{height:120px;background:var(--fm-bg2,#0F0F0F);display:flex;align-items:center;justify-content:center;font-size:40px;color:var(--fm-s,#3A3530);position:relative;background-size:cover;background-position:center}
.mi-report-type{position:absolute;top:8px;right:8px;background:rgba(0,0,0,.6);color:#FFD700;font-size:10px;padding:2px 8px;border-radius:6px;text-transform:uppercase;letter-spacing:.4px}
.mi-report-body{padding:14px}
.mi-report-title{color:var(--fm-h,#F5F0E8);font-weight:700;font-size:13px;line-height:1.4;margin-bottom:6px}
.mi-report-meta{color:var(--fm-s,#6B6356);font-size:10px;margin-bottom:8px}
.mi-report-excerpt{color:var(--fm-m,#A09888);font-size:11px;line-height:1.5;display:-webkit-box;-webkit-line-clamp:3;-webkit-box-orient:vertical;overflow:hidden}
.mi-report-stats{display:flex;gap:10px;font-size:10px;color:var(--fm-s,#6B6356);margin-top:8px;padding-top:8px;border-top:1px dashed var(--fm-br,#1E1E1E)}

/* ===== MOBILE — feels-like-an-app ===== */
@media (max-width: 768px) {
  .mi-container { padding-top: 60px !important; padding-bottom: calc(80px + env(safe-area-inset-bottom, 0px)) !important; padding-left: 12px !important; padding-right: 12px !important; }
  .mi-card { padding: 12px !important; border-radius: 10px !important; }
  .mi-report-body { padding: 12px; }
  .mi-report-title { font-size: 12px; }
  .mi-report-meta { font-size: 9px; }
  .mi-report-excerpt { font-size: 10px; }
}
</style>

<script>
/* eslint-disable */
import { execute, executeJson, flocker, uploads } from "@/api";

export default {
  name: "MarketIntelligence",
  data(){return{
    darkMode: (document.documentElement.getAttribute('data-theme') || localStorage.getItem('flockr_theme') || 'dark') !== 'light',
    themeObserver: null,
    species: [], counties: [],
    filter: { speciesId:'', countyId:'', periodType:'' },
    prices: [], loadingPrices: false,
    reports: [], loadingReports: false,
    trendKey: '', trendLabel: '', trendSeries: [], loadingTrend: false,
    chartW: 600, chartH: 140,
    // Dev-Requests #3 — auto-price grid archived until platform data is denser.
    // Admins can flip localStorage.flockr_show_price_grid to '1' to preview.
    showPriceGrid: (typeof localStorage !== 'undefined' && localStorage.getItem('flockr_show_price_grid') === '1'),
    insights: [], loadingInsights: false,
    marketDays: [], loadingMarketDays: false,
    mdExpandedId: null, mdDetail: null, loadingMdDetail: false,
    histYears: [], histYear: null,
    histMonths: ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec']
  }},
  computed:{
    histActive(){
      var y = this.histYear;
      return this.histYears.find(function(h){ return h.year === y; }) || {};
    },
    themePageStyle(){return this.darkMode?'background:#0A0A0A;min-height:100vh;color:#F5F0E8;':'background:#F7F5F2;min-height:100vh;color:#1A1A1A;'},
    chartPoints(){
      // Map series to SVG coords. Linear x, padded y.
      var series = this.trendSeries; if (!series.length) return [];
      var minP = Math.min.apply(null, series.map(function(s){return Number(s.avg_price);}));
      var maxP = Math.max.apply(null, series.map(function(s){return Number(s.avg_price);}));
      if (maxP === minP) maxP = minP + 1;
      var padTop = 12, padBot = 22, padX = 12;
      var w = this.chartW, h = this.chartH;
      var stepX = (w - padX*2) / Math.max(1, series.length - 1);
      var self = this;
      return series.map(function(s, i){
        var t = (Number(s.avg_price) - minP) / (maxP - minP);
        return { x: padX + stepX * i, y: padTop + (1 - t) * (h - padTop - padBot) };
      });
    },
    chartLine(){ return this.chartPoints.map(function(p){return p.x.toFixed(1)+','+p.y.toFixed(1);}).join(' '); },
    chartArea(){
      var pts = this.chartPoints; if (!pts.length) return '';
      var bot = (this.chartH - 12).toFixed(1);
      var s = pts[0].x.toFixed(1) + ',' + bot + ' ';
      s += pts.map(function(p){return p.x.toFixed(1)+','+p.y.toFixed(1);}).join(' ');
      s += ' ' + pts[pts.length-1].x.toFixed(1) + ',' + bot;
      return s;
    },
    trendStats(){
      var s = this.trendSeries; if (!s.length) return { min:0, max:0, avg:0, changePct:0 };
      var prices = s.map(function(x){return Number(x.avg_price);});
      var min = Math.min.apply(null, prices);
      var max = Math.max.apply(null, prices);
      var avg = Math.round(prices.reduce(function(a,b){return a+b;}, 0) / prices.length);
      var first = prices[0], last = prices[prices.length-1];
      var changePct = first > 0 ? ((last - first) / first) * 100 : 0;
      return { min: min, max: max, avg: avg, changePct: changePct };
    }
  },
  mounted(){
    this._applyThemeOnce();
    var self = this;
    this.themeObserver = new MutationObserver(function(){ try { self._applyThemeOnce(); } catch(e) {} });
    this.themeObserver.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });

    this.loadReferenceData();
    this.loadInsights();
    this.loadMarketDays();
    this.loadHistory();
    if (this.showPriceGrid) this.loadPrices();
    this.loadReports();
  },
  beforeUnmount(){
    if (this.themeObserver) { this.themeObserver.disconnect(); this.themeObserver = null; }
  },
  methods:{
    _applyThemeOnce(){
      var current = document.documentElement.getAttribute('data-theme');
      var desired = (localStorage.getItem('flockr_theme') || current || 'dark');
      if (current !== desired) document.documentElement.setAttribute('data-theme', desired);
      this.darkMode = desired !== 'light';
    },
    fNum(n){ return Number(n||0).toLocaleString(); },
    imgUrl(u){ if(!u) return ''; if(u.indexOf('http')===0) return u; return uploads + String(u).replace(/^uploads\//, ''); },
    humanizeSource(s){
      var map = { platform_transactions:'Platform transactions', market_survey:'Market survey', government_data:'Government data', aggregated:'Aggregated' };
      return map[s] || s;
    },
    async loadReferenceData(){
      // Pull species + counties for the dropdowns. Both endpoints are public.
      try {
        var sp = await executeJson({ function:'getAnimalSpecies' }, flocker);
        if (sp.data && sp.data.success) this.species = (sp.data.data && sp.data.data.species) || sp.data.data || [];
        var co = await executeJson({ function:'getCounties' }, flocker);
        if (co.data && co.data.success) this.counties = (co.data.data && co.data.data.counties) || co.data.data || [];
      } catch(e){}
    },
    async loadPrices(){
      this.loadingPrices = true;
      try {
        var params = { function:'getMarketPrices' };
        if (this.filter.speciesId) params.speciesId = this.filter.speciesId;
        if (this.filter.countyId)  params.countyId  = this.filter.countyId;
        if (this.filter.periodType) params.periodType = this.filter.periodType;
        var r = await executeJson(params, flocker);
        if (r.data && r.data.success) this.prices = (r.data.data && r.data.data.prices) || [];
      } catch(e){ console.error(e); }
      this.loadingPrices = false;
    },
    async loadReports(){
      this.loadingReports = true;
      try {
        var r = await executeJson({ function:'getMarketReports' }, flocker);
        if (r.data && r.data.success) this.reports = (r.data.data && r.data.data.reports) || [];
      } catch(e){ console.error(e); }
      this.loadingReports = false;
    },
    async loadMarketDays(){
      this.loadingMarketDays = true;
      try {
        var r = await executeJson({ function:'getMarketDayReports', limit: 12 }, flocker);
        if (r.data && r.data.success) this.marketDays = (r.data.data && r.data.data.reports) || [];
      } catch(e){ console.error(e); }
      this.loadingMarketDays = false;
    },
    async toggleMarketDay(d){
      if (this.mdExpandedId === d.id) { this.mdExpandedId = null; this.mdDetail = null; return; }
      this.mdExpandedId = d.id;
      this.mdDetail = null; this.loadingMdDetail = true;
      try {
        var r = await executeJson({ function:'getMarketDayReportDetail', reportId: d.id }, flocker);
        if (r.data && r.data.success) this.mdDetail = (r.data.data && r.data.data.report) || null;
      } catch(e){ console.error(e); }
      this.loadingMdDetail = false;
    },
    mdMovements(scope){
      if (!this.mdDetail || !this.mdDetail.movements) return [];
      return this.mdDetail.movements.filter(function(m){ return m.scope === scope; });
    },
    async loadHistory(){
      try {
        var r = await executeJson({ function:'getMarketMovementHistory' }, flocker);
        if (r.data && r.data.success) {
          var years = ((r.data.data && r.data.data.years) || []).map(function(y){ y.year = Number(y.year); return y; });
          this.histYears = years;
          if (years.length) this.histYear = years[0].year;
        }
      } catch(e){ console.error(e); }
    },
    fmtMdDate(d){
      try {
        return new Date(String(d) + 'T00:00:00').toLocaleDateString('en-KE', { day:'numeric', month:'short', year:'numeric' });
      } catch(e){ return d; }
    },
    async loadInsights(){
      this.loadingInsights = true;
      try {
        var params = { function:'getExpertInsights', limit: 24 };
        if (this.filter.speciesId) params.speciesId = this.filter.speciesId;
        if (this.filter.countyId)  params.countyId  = this.filter.countyId;
        var r = await executeJson(params, flocker);
        if (r.data && r.data.success) this.insights = (r.data.data && r.data.data.insights) || [];
      } catch(e){ console.error(e); }
      this.loadingInsights = false;
    },
    fmtDate(ts){
      if (!ts) return '';
      try {
        var d = new Date(String(ts).replace(' ','T'));
        var now = new Date();
        var diff = (now - d) / 1000;
        if (diff < 60) return 'just now';
        if (diff < 3600) return Math.floor(diff/60) + 'm ago';
        if (diff < 86400) return Math.floor(diff/3600) + 'h ago';
        if (diff < 86400*7) return Math.floor(diff/86400) + 'd ago';
        return d.toISOString().slice(0,10);
      } catch(e) { return String(ts).slice(0,10); }
    },
    async loadTrend(p){
      var key = p.species_id + '-' + p.county_id + '-' + (p.breed_id||0);
      // Click again to collapse
      if (this.trendKey === key) { this.trendKey = ''; this.trendSeries = []; return; }
      this.trendKey = key;
      this.trendLabel = p.species_name + (p.breed_name ? ' · ' + p.breed_name : '') + ' in ' + p.county_name;
      this.loadingTrend = true; this.trendSeries = [];
      try {
        var params = { function:'getMarketPriceTrend', speciesId: p.species_id, countyId: p.county_id, months: 12 };
        if (p.breed_id) params.breedId = p.breed_id;
        var r = await executeJson(params, flocker);
        if (r.data && r.data.success) this.trendSeries = (r.data.data && r.data.data.series) || [];
      } catch(e){ console.error(e); }
      this.loadingTrend = false;
    },
    openReport(r){
      this.$router.push('/market/report/' + (r.slug || r.id));
    }
  }
};
</script>
